import { useCallback, useEffect, useState } from 'react'
import type { NoteType, User } from '@shared/types'
import { get, patch, post, put } from '../../lib/api'
import { useSession } from '../../state/session'
import { useToast } from '../../state/toast'
import { Button, ErrorNote, Field, SectionCard, SectionHeading, Sheet, Spinner } from '../../components/ui'

const TIMEZONES = [
  'America/St_Johns', 'America/Halifax', 'America/Toronto', 'America/Winnipeg',
  'America/Regina', 'America/Edmonton', 'America/Vancouver',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'Europe/London', 'Europe/Dublin', 'Australia/Sydney', 'Pacific/Auckland', 'UTC',
]

export default function AdminSettings() {
  const { refresh } = useSession()
  const toast = useToast()
  const [settings, setSettings] = useState<Record<string, string> | null>(null)
  const [noteTypes, setNoteTypes] = useState<NoteType[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [broadcast, setBroadcast] = useState(false)

  const load = useCallback(async () => {
    const [settingsRes, typesRes, usersRes] = await Promise.all([
      get<{ settings: Record<string, string> }>('/api/admin/settings'),
      get<{ noteTypes: NoteType[] }>('/api/admin/note-types'),
      get<{ users: User[] }>('/api/admin/users'),
    ])
    setSettings(settingsRes.settings)
    setNoteTypes(typesRes.noteTypes)
    setUsers(usersRes.users.filter((u) => u.active))
  }, [])

  useEffect(() => {
    void load().catch(() => setSettings({}))
  }, [load])

  if (!settings) return <div className="py-16"><Spinner /></div>

  const update = (key: string, value: string) =>
    setSettings((current) => ({ ...(current ?? {}), [key]: value }))

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      await put('/api/admin/settings', settings)
      await refresh()
      toast.success('Settings saved')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save settings')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      {/* Everything in this card saves together — the footer button makes the
          scope of "Save" visible instead of implied. */}
      <SectionCard
        title="Company settings"
        footer={
          <div className="space-y-3">
            <ErrorNote>{error}</ErrorNote>
            <Button variant="primary" className="w-full" loading={busy} onClick={() => void save()}>
              Save settings
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Field label="Company name">
            <input
              className="field"
              value={settings['org.name'] ?? ''}
              onChange={(e) => update('org.name', e.target.value)}
            />
          </Field>
          <Field label="Timezone" hint="All reminder times and day boundaries use this.">
            <select
              className="field"
              value={settings['org.timezone'] ?? 'UTC'}
              onChange={(e) => update('org.timezone', e.target.value)}
            >
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </Field>
        </div>

        <div className="space-y-4 pt-2">
          <p className="label">Site arrival detection</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ignore GPS worse than (m)">
              <input
                className="field font-mono"
                type="number"
                min={20}
                max={1000}
                value={settings['geo.min_accuracy_m'] ?? '100'}
                onChange={(e) => update('geo.min_accuracy_m', e.target.value)}
              />
            </Field>
            <Field label="Wait before prompting (s)">
              <input
                className="field font-mono"
                type="number"
                min={0}
                max={1800}
                step={30}
                value={settings['geo.dwell_seconds'] ?? '120'}
                onChange={(e) => update('geo.dwell_seconds', e.target.value)}
              />
            </Field>
          </div>
          <p className="text-xs text-graphite-2">
            Higher accuracy tolerance catches more arrivals but prompts more often; the wait is
            how long somebody must stay inside a geofence before being asked to switch sites.
          </p>
        </div>

        <div className="space-y-4 pt-2">
          <p className="label">Photos</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Longest edge (px)">
              <input
                className="field font-mono"
                type="number"
                min={640}
                max={4000}
                step={100}
                value={settings['photo.max_dimension'] ?? '1600'}
                onChange={(e) => update('photo.max_dimension', e.target.value)}
              />
            </Field>
            <Field label="JPEG quality" hint="0.4 to 0.95">
              <input
                className="field font-mono"
                type="number"
                min={0.4}
                max={0.95}
                step={0.05}
                value={settings['photo.quality'] ?? '0.75'}
                onChange={(e) => update('photo.quality', e.target.value)}
              />
            </Field>
          </div>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 size-5 accent-[var(--wm-brand-deep)]"
              checked={(settings['note.encourage_photo'] ?? 'true') === 'true'}
              onChange={(e) => update('note.encourage_photo', String(e.target.checked))}
            />
            <span className="text-sm">
              Ask for a photo on scheduled updates
              <span className="block text-xs text-graphite-2">
                Suggested, never required — a text-only update always beats no update.
              </span>
            </span>
          </label>
        </div>
      </SectionCard>

      <SectionCard title="Update types">
        <p className="text-sm text-graphite-2">
          Types marked “needs attention” stay in the review queue until the office acknowledges
          them. Changes here save immediately.
        </p>
        <NoteTypeEditor noteTypes={noteTypes} onChanged={load} />
      </SectionCard>

      <SectionCard title="Send a message">
        <p className="text-sm text-graphite-2">
          A one-off push to the crew — a weather call, a site closure, a schedule change.
        </p>
        <Button className="w-full" onClick={() => setBroadcast(true)}>
          Compose a notification
        </Button>
      </SectionCard>

      {broadcast && <BroadcastSheet users={users} onClose={() => setBroadcast(false)} />}
    </div>
  )
}

function NoteTypeEditor({
  noteTypes, onChanged,
}: {
  noteTypes: NoteType[]
  onChanged: () => Promise<void>
}) {
  const toast = useToast()
  const [adding, setAdding] = useState(false)
  const [label, setLabel] = useState('')
  const [color, setColor] = useState('#6B6560')
  const [needsAck, setNeedsAck] = useState(false)
  const [visibleToAll, setVisibleToAll] = useState(false)

  const toggleShared = async (type: NoteType) => {
    try {
      await patch(`/api/admin/note-types/${type.key}`, { visibleToAll: !type.visibleToAll })
      await onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update')
    }
  }

  const toggleAck = async (type: NoteType) => {
    try {
      await patch(`/api/admin/note-types/${type.key}`, { needsAck: !type.needsAck })
      await onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update')
    }
  }

  const toggleActive = async (type: NoteType) => {
    try {
      await patch(`/api/admin/note-types/${type.key}`, { active: !type.active })
      await onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update')
    }
  }

  const add = async () => {
    const key = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')
    if (!key) return
    try {
      await post('/api/admin/note-types', { key, label: label.trim(), color, needsAck, visibleToAll })
      setAdding(false)
      setLabel('')
      setNeedsAck(false)
      setVisibleToAll(false)
      await onChanged()
      toast.success('Type added')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add the type')
    }
  }

  return (
    <div>
      {noteTypes.map((type) => (
        <div
          key={type.key}
          className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line py-2.5 ${type.active ? '' : 'opacity-55'}`}
        >
          <span className="size-3 shrink-0 rounded-full" style={{ background: type.color }} aria-hidden />
          <span className="min-w-0 flex-1 truncate font-semibold">{type.label}</span>
          {/* Shared types show in everyone's Log, whoever posted them. */}
          <label className="flex shrink-0 items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              className="size-4 accent-[var(--wm-brand-deep)]"
              checked={type.visibleToAll}
              onChange={() => void toggleShared(type)}
            />
            Everyone sees
          </label>
          <label className="flex shrink-0 items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              className="size-4 accent-[var(--wm-brand-deep)]"
              checked={type.needsAck}
              onChange={() => void toggleAck(type)}
            />
            Needs attention
          </label>
          <button
            type="button"
            onClick={() => void toggleActive(type)}
            className="shrink-0 link text-xs"
          >
            {type.active ? 'Hide' : 'Show'}
          </button>
        </div>
      ))}

      {adding ? (
        <div className="mt-3 space-y-3 rounded bg-well/50 p-3">
          <Field label="Label">
            <input
              className="field"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Inspection"
            />
          </Field>
          <Field label="Colour">
            <input
              type="color"
              className="field h-12 p-1"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-5 accent-[var(--wm-brand-deep)]"
              checked={needsAck}
              onChange={(e) => setNeedsAck(e.target.checked)}
            />
            Needs an acknowledgement
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-5 accent-[var(--wm-brand-deep)]"
              checked={visibleToAll}
              onChange={(e) => setVisibleToAll(e.target.checked)}
            />
            Everyone sees it in their Log
          </label>
          <div className="flex gap-2">
            <Button className="flex-1" onClick={() => setAdding(false)}>Cancel</Button>
            <Button variant="primary" className="flex-1" onClick={() => void add()}>Add</Button>
          </div>
        </div>
      ) : (
        <Button className="mt-3 w-full" onClick={() => setAdding(true)}>Add a type</Button>
      )}
    </div>
  )
}

function BroadcastSheet({ users, onClose }: { users: User[]; onClose: () => void }) {
  const toast = useToast()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  const send = async () => {
    setBusy(true)
    try {
      const result = await post<{ sent: number; failed: number }>('/api/admin/notify', {
        title,
        body,
        userIds: selected.size ? [...selected] : undefined,
      })
      toast.success(`Sent to ${result.sent} device${result.sent === 1 ? '' : 's'}`)
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send')
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Send a notification"
      footer={
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          loading={busy}
          disabled={!title.trim() || !body.trim()}
          onClick={() => void send()}
        >
          Send {selected.size ? `to ${selected.size}` : 'to everyone'}
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label="Title" required>
          <input
            className="field"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Site closed today"
          />
        </Field>
        <Field label="Message" required>
          <textarea
            className="field"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Riverbend is shut for the storm. Do not travel — we will call this morning."
          />
        </Field>
        <div>
          <SectionHeading>Who (leave empty for everyone)</SectionHeading>
          <div className="space-y-1">
            {users.map((user) => (
              <label key={user.id} className="flex items-center gap-2 border-b border-line py-1.5">
                <input
                  type="checkbox"
                  className="size-5 accent-[var(--wm-brand-deep)]"
                  checked={selected.has(user.id)}
                  onChange={(e) => {
                    const next = new Set(selected)
                    if (e.target.checked) next.add(user.id)
                    else next.delete(user.id)
                    setSelected(next)
                  }}
                />
                <span className="text-sm">{user.name}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Sheet>
  )
}
